import { ReactElement, useState, useEffect, useRef } from 'react'
import { track } from '../../services/analytics/analytics'
import { Redirect, useHistory } from 'react-router-dom'
import { App, Alert } from 'antd'
import { useIntl } from 'react-intl'
import axios from 'axios'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectLocaleSlice } from '../../store/locale-slice'
import { FrontendUrls } from '../../types/urls'
import { OnboardingLayout, LocationStep, SetupSummaryCard, DepartmentsStep, ImportStep } from '../../components/onboarding-wizard'
import type { OnboardingStep, IOnboardingLocation, IOnboardingLeaveType, IOnboardingDepartment } from '../../components/onboarding-wizard'
import { reconcileWithNewLocation, cleanupLeaveTypesForRemovedLocation } from '../../components/onboarding-wizard/leave-type-mapping'
import { useLeaveTypesApi } from '../../services/api/leave-type-templates-hook'
import { useWizardDepartmentInit } from '../../services/api/wizard-departments-hook'
import {
  getWizardState,
  saveWizardStep,
  saveWizardLocations,
  saveWizardLeaveTypes,
  markWizardLeaveTypesInitialized,
  markWizardDepartmentsInitialized,
  saveWizardDepartments,
  initWizardIdentity,
  isWizardDismissed,
  clearWizardState,
  markWizardSavedToDB,
  saveWizardPlan,
  resetWizardLeaveTypes,
  saveWizardInitialDefaultLeaveTypeIds,
} from '../../util/onboarding-wizard-storage'
import { getWizardPlanLimits } from '../../util/wizard-plan-limits'
import CircularProgress from '../../components/circular-progress'
import LeaveTypesStep from '../../components/onboarding-wizard/LeaveTypesStep'
import Api from '@vacationtracker/shared/services/api'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import { getTimeZoneOrDefaultToUtc } from '@vacationtracker/shared/functions/timezone'
import { getEffectivePlan } from '@vacationtracker/shared/functions/plan-boost'
import { useManualQuery } from 'graphql-hooks'
import { getTeamById } from '../../graphql/custom-queries'
import { ApproversLevelEnum } from '@vacationtracker/shared/types/team'
import type { IGetHolidaysResponse } from '@vacationtracker/shared/types/holidays'
import { countryName } from '@vacationtracker/shared/data/countries'
import { timezoneForPlace } from '@vacationtracker/shared/data/country-timezones'
import IntlMessages from '../../util/IntlMessages'
import { wizardHolidaysImportEvent } from './holidays-import-event'

const uid = () => crypto.randomUUID()

const OnboardingWizard = (): ReactElement => {
  const history = useHistory()
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser, isLoadingAuthUser } = useAppSelector(selectAuthUserSlice)
  // Signup seeds the holidays in the user's locale, and library names differ per locale, so re-imports use the same one.
  const holidayLocale = useAppSelector(selectLocaleSlice).locale.locale

  const [currentStep, setCurrentStep] = useState<OnboardingStep>('locations')
  const [savedToDB, setSavedToDB] = useState(false)
  const [locations, setLocations] = useState<IOnboardingLocation[]>([])
  const [leaveTypes, setLeaveTypes] = useState<IOnboardingLeaveType[]>([])
  const [departments, setDepartments] = useState<IOnboardingDepartment[]>([])
  const [leaveTypesInitialized, setLeaveTypesInitialized] = useState(false)
  const [departmentsInitialized, setDepartmentsInitialized] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitProgress, setSubmitProgress] = useState(0)
  const stateInitialized = useRef(false)

  const limits = getWizardPlanLimits(getEffectivePlan(authCompany))
  const overLocationLimit = locations.length > limits.maxLocations
  const overLeaveTypeLimit = leaveTypes.length > limits.maxLeaveTypes
  const overDepartmentLimit = departments.length > limits.maxDepartments
  const anyLimitExceeded = overLocationLimit || overLeaveTypeLimit || overDepartmentLimit
  const { data: apiData } = useLeaveTypesApi()
  const [getTeamByIdQuery] = useManualQuery<{ getTeam: any }>(getTeamById)

  useEffect(() => {
    if (isLoadingAuthUser || stateInitialized.current || isWizardDismissed(authUser?.id, authCompany?.id)) return
    stateInitialized.current = true

    initWizardIdentity(authUser.id, authCompany?.id ?? '')

    const saved = getWizardState(authUser.id, authCompany?.id)
    if (saved) {
      setCurrentStep(saved.currentStep)
      setSavedToDB(saved.savedToDB)
      setDepartmentsInitialized(saved.departmentsInitialized && saved.departments.length > 0)

      if (saved.locations.length > 0) {
        setLocations(saved.locations)
        if (saved.leaveTypes.length > 0) {
          setLeaveTypes(saved.leaveTypes)
          saveWizardInitialDefaultLeaveTypeIds(saved.leaveTypes.filter((lt: IOnboardingLeaveType) => lt.isFromDefault).map((lt: IOnboardingLeaveType) => lt.id))
        }
      } else {
        seedSignupLocation()
      }

      setLeaveTypesInitialized(saved.leaveTypesInitialized && saved.leaveTypes.length > 0)

      if (saved.departments.length > 0) {
        setDepartments(saved.departments)
      }
    } else {
      seedSignupLocation()
    }
    saveWizardPlan(authCompany?.plan ?? '')
  }, [isLoadingAuthUser, authUser?.id, authCompany?.id, authCompany?.plan])

  const needsDepartmentInit = currentStep === 'departments' && !departmentsInitialized && departments.length === 0

  const { data: fetchedDepartments, isLoading: isDepartmentsLoading, isError: isDepartmentsError } = useWizardDepartmentInit(needsDepartmentInit)

  useEffect(() => {
    if (!needsDepartmentInit || departmentsInitialized) return
    if (fetchedDepartments !== undefined) {
      setDepartments(fetchedDepartments)
      setDepartmentsInitialized(true)
      saveWizardDepartments(fetchedDepartments)
      markWizardDepartmentsInitialized()
    } else if (isDepartmentsError) {
      setDepartments([])
      setDepartmentsInitialized(true)
      saveWizardDepartments([])
      markWizardDepartmentsInitialized()
    }
  }, [fetchedDepartments, isDepartmentsError, departmentsInitialized, needsDepartmentInit])

  const seedSignupLocation = () => {
    const signupLoc: IOnboardingLocation = {
      id: authUser.location.id,
      name: authUser.location.name,
      countryCode: authUser.location.countryCode ?? '',
      subdivisionCode: authUser.location.subdivisionCode ?? null,
      isFromSignup: true,
    }
    setLocations([signupLoc])
    saveWizardLocations([signupLoc])
  }

  if (isWizardDismissed(authUser?.id, authCompany?.id)) {
    return <Redirect to={FrontendUrls.dashboard} />
  }

  if (isLoadingAuthUser) {
    return <CircularProgress />
  }

  const handleStepClick = (step: OnboardingStep): void => {
    if (isSubmitting) return
    setCurrentStep(step)
    saveWizardStep(step)
  }

  const removeZeroDayDefaultOverrides = (lts: IOnboardingLeaveType[]) =>
    lts.map((lt) => {
      const name = lt.name.toLowerCase()
      if (!name.includes('paid time off') && !name.includes('sick')) return lt
      return { ...lt, overrides: lt.overrides.filter((o) => o.daysPerYear !== 0) }
    })

  const handleLocationAdd = (loc: Pick<IOnboardingLocation, 'name' | 'countryCode' | 'subdivisionCode'>) => {
    const newLoc = { ...loc, id: uid() }
    const next = [...locations, newLoc]
    setLocations(next)
    saveWizardLocations(next)

    if (leaveTypesInitialized && leaveTypes.length > 0 && apiData) {
      let reconciled = reconcileWithNewLocation(leaveTypes, newLoc, apiData)
      if (reconciled !== leaveTypes) {
        reconciled = removeZeroDayDefaultOverrides(reconciled)
        setLeaveTypes(reconciled)
        saveWizardLeaveTypes(reconciled)
      }
    }
  }

  const handleLocationRemove = (id: string) => {
    const next = locations.filter((l) => l.id !== id)
    setLocations(next)
    saveWizardLocations(next)

    if (leaveTypes.length > 0) {
      const cleaned = cleanupLeaveTypesForRemovedLocation(leaveTypes, id)
      setLeaveTypes(cleaned)
      saveWizardLeaveTypes(cleaned)
    }
  }

  const handleLocationConfirm = () => {
    setCurrentStep('leaveTypes')
    saveWizardStep('leaveTypes')
  }

  const handleLeaveTypesUpdate = (updated: IOnboardingLeaveType[]) => {
    setLeaveTypes(updated)
    saveWizardLeaveTypes(updated)
  }

  const handleLeaveTypesInitialized = (initialized: IOnboardingLeaveType[]) => {
    let final = initialized
    if (apiData) {
      for (const loc of locations.filter((l) => !l.isFromSignup)) {
        final = reconcileWithNewLocation(final, loc, apiData)
      }
      final = removeZeroDayDefaultOverrides(final)
    }
    saveWizardInitialDefaultLeaveTypeIds(final.filter((lt) => lt.isFromDefault).map((lt) => lt.id))
    setLeaveTypes(final)
    setLeaveTypesInitialized(true)
    saveWizardLeaveTypes(final)
    markWizardLeaveTypesInitialized()
  }

  const handleLeaveTypesConfirm = () => {
    setCurrentStep('departments')
    saveWizardStep('departments')
  }

  const handleDepartmentsUpdate = (updated: IOnboardingDepartment[]) => {
    setDepartments(updated)
    saveWizardDepartments(updated)
  }

  const handleSkipForNow = () => {
    track('SETUP_WIZARD_SKIPPED', { step: currentStep })
    history.push(FrontendUrls.dashboard)
  }

  const handleSkip = () => {
    track('SETUP_WIZARD_DISMISSED', {})
    clearWizardState(authUser.id, authCompany?.id)
    history.push(FrontendUrls.dashboard)
  }

  const handleWizardComplete = () => {
    clearWizardState(authUser.id, authCompany?.id)
    history.push(`${FrontendUrls.dashboard}?wizardCompleted=true`)
  }

  const postWithRetry = async (body: object): Promise<any> => {
    return await Api.post('/core/event', body)
  }

  const fetchHolidaysForLocation = async (loc: IOnboardingLocation, year: number): Promise<IGetHolidaysResponse[]> => {
    const tokens = await getAuthTokens()
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/core/holidays`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens?.idToken}`,
      },
      params: {
        year,
        countryIso: loc.countryCode,
        country: countryName(loc.countryCode) ?? loc.countryCode,
        stateIso: loc.subdivisionCode || undefined,
        locale: holidayLocale,
      },
    })
    response.data.forEach((h: IGetHolidaysResponse) => {
      h.date = h.date.slice(0, 19)
    })
    return response.data as IGetHolidaysResponse[]
  }

  // Called after departments are confirmed - point of no return
  const submitWizard = async () => {
    setIsSubmitting(true)
    setSubmitProgress(0)

    try {
      const locationIdMap = new Map<string, string>()

      // Step 1: Create new locations (non-signup)
      const newLocations = locations.filter((l) => !l.isFromSignup)
      for (const loc of newLocations) {
        const resp = await postWithRetry({
          eventType: 'LOCATION_CREATED',
          eventGroup: 'LOCATION',
          name: loc.name,
          countryCode: loc.countryCode,
          subdivisionCode: loc.subdivisionCode,
          days: [1, 2, 3, 4, 5],
          workHours: null,
          // The location's own place decides this; the browser's zone is only the fallback for a
          // country we cannot map.
          timezone: timezoneForPlace({ countryCode: loc.countryCode, subdivisionCode: loc.subdivisionCode ?? null }) ?? getTimeZoneOrDefaultToUtc(),
          resetQuotas: 'FISCAL_YEAR',
          yearStartDay: 1,
          yearStartMonth: 1,
          firstDayOfWeek: 1,
          isDefault: false,
          users: [],
        })
        locationIdMap.set(loc.id, resp.locationId)
      }
      setSubmitProgress(15)

      const resolveLocId = (id: string) => locationIdMap.get(id) ?? id
      const allLocationServerIds = locations.map((l) => resolveLocId(l.id))

      // Step 2: Delete default leave types that were removed in the wizard
      const currentDefaultIds = new Set(leaveTypes.filter((lt) => lt.isFromDefault).map((lt) => lt.id))
      const savedState = getWizardState(authUser.id, authCompany?.id)
      const deletedDefaultIds = (savedState?.initialDefaultLeaveTypeIds ?? []).filter((id: string) => !currentDefaultIds.has(id))
      for (const id of deletedDefaultIds) {
        await postWithRetry({
          eventType: 'LEAVE_TYPE_DELETED',
          eventGroup: 'LEAVE_TYPE',
          leaveTypeId: id,
        })
      }

      // Step 2b: Update remaining default leave types (name/color/active only - policy is separate)
      const defaultLeaveTypes = leaveTypes.filter((lt) => lt.isFromDefault)
      for (const lt of defaultLeaveTypes) {
        await postWithRetry({
          eventType: 'LEAVE_TYPE_UPDATED',
          eventGroup: 'LEAVE_TYPE',
          leaveTypeId: lt.id,
          name: lt.name,
          color: lt.color,
          isActive: lt.isActive,
          calendarStatus: 'Busy',
        })
      }

      // Step 2b: Update policy on signup location for default leave types
      const signupLocServerId = locations.find((l) => l.isFromSignup)?.id
      if (signupLocServerId) {
        for (const lt of defaultLeaveTypes) {
          const appliesHere = lt.appliesToAll || lt.appliesTo.includes(signupLocServerId)
          if (!appliesHere) {
            await postWithRetry({
              eventType: 'LEAVE_POLICY_DISABLED',
              eventGroup: 'LEAVE_POLICY',
              leaveTypeId: lt.id,
              locationId: signupLocServerId,
            })
            continue
          }
          const override = lt.overrides.find((o) => o.locationId === signupLocServerId)
          await postWithRetry({
            eventType: 'LEAVE_POLICY_ENABLED',
            eventGroup: 'LEAVE_POLICY',
            leaveTypeId: lt.id,
            locationId: signupLocServerId,
            daysPerYear: override ? (override.hasUnlimitedDays ? 0 : override.daysPerYear) : lt.hasUnlimitedDays ? 0 : lt.daysPerYear,
            hasUnlimitedDays: override ? override.hasUnlimitedDays : lt.hasUnlimitedDays,
            isApprovalRequired: override ? override.isApprovalRequired : lt.isApprovalRequired,
            shortestLeaveInterval: override ? override.shortestLeaveIntervalEnum : lt.shortestLeaveIntervalEnum,
            negativeBallanceAllowed: false,
            maxRolloverDays: 0,
            quotaSetIn: 'days',
            hideLeaveType: false,
            accrualType: 'NONE',
            isReasonRequired: false,
            cancelUserSettings: false,
            allowLeaveInPast: false,
            broughtForwardExpirationSettings: { enabled: false },
            toil: false,
            version: 3,
          })
        }
      }
      setSubmitProgress(30)

      // Step 3: Create new leave types
      // locationIds is intentionally empty - the background Lambda would overwrite per-location
      // policies if we included them here. We send LEAVE_POLICY_ENABLED per location manually instead.
      const newLeaveTypes = leaveTypes.filter((lt) => !lt.isFromDefault)
      for (const lt of newLeaveTypes) {
        const locationIds = lt.appliesToAll ? allLocationServerIds : lt.appliesTo.map(resolveLocId).filter(Boolean)

        const resp = await postWithRetry({
          eventType: 'LEAVE_TYPE_CREATED',
          eventGroup: 'LEAVE_TYPE',
          name: lt.name,
          color: lt.color,
          isActive: true,
          locationIds: [],
          calendarStatus: 'Busy',
          leavePolicy: {
            negativeBallanceAllowed: false,
            daysPerYear: lt.hasUnlimitedDays ? 0 : lt.daysPerYear,
            maxRolloverDays: 0,
            quotaSetIn: 'days',
            hasUnlimitedDays: lt.hasUnlimitedDays,
            shortestLeaveInterval: lt.shortestLeaveIntervalEnum,
            hideLeaveType: false,
            isApprovalRequired: lt.isApprovalRequired,
            isReasonRequired: false,
            allowLeaveInPast: false,
          },
          version: 2,
        })

        for (const serverLocId of locationIds) {
          const override = lt.overrides.find((o) => resolveLocId(o.locationId) === serverLocId)
          await postWithRetry({
            eventType: 'LEAVE_POLICY_ENABLED',
            eventGroup: 'LEAVE_POLICY',
            leaveTypeId: resp.leaveTypeId,
            locationId: serverLocId,
            daysPerYear: override ? (override.hasUnlimitedDays ? 0 : override.daysPerYear) : lt.hasUnlimitedDays ? 0 : lt.daysPerYear,
            hasUnlimitedDays: override ? override.hasUnlimitedDays : lt.hasUnlimitedDays,
            isApprovalRequired: override ? override.isApprovalRequired : lt.isApprovalRequired,
            shortestLeaveInterval: override ? override.shortestLeaveIntervalEnum : lt.shortestLeaveIntervalEnum,
            negativeBallanceAllowed: false,
            maxRolloverDays: 0,
            quotaSetIn: 'days',
            hideLeaveType: false,
            accrualType: 'NONE',
            isReasonRequired: false,
            cancelUserSettings: false,
            allowLeaveInPast: false,
            broughtForwardExpirationSettings: { enabled: false },
            toil: false,
            version: 3,
          })
        }
      }
      setSubmitProgress(45)

      // Step 4: Enable/disable policies for default LTs on new locations
      for (const lt of defaultLeaveTypes) {
        for (const loc of newLocations) {
          const serverLocId = locationIdMap.get(loc.id)!
          const appliesHere = lt.appliesToAll || lt.appliesTo.includes(loc.id)
          if (!appliesHere) continue
          const override = lt.overrides.find((o) => o.locationId === loc.id)
          await postWithRetry({
            eventType: 'LEAVE_POLICY_ENABLED',
            eventGroup: 'LEAVE_POLICY',
            leaveTypeId: lt.id,
            locationId: serverLocId,
            daysPerYear: override ? (override.hasUnlimitedDays ? 0 : override.daysPerYear) : lt.hasUnlimitedDays ? 0 : lt.daysPerYear,
            hasUnlimitedDays: override ? override.hasUnlimitedDays : lt.hasUnlimitedDays,
            isApprovalRequired: override ? override.isApprovalRequired : lt.isApprovalRequired,
            shortestLeaveInterval: override ? override.shortestLeaveIntervalEnum : lt.shortestLeaveIntervalEnum,
            negativeBallanceAllowed: false,
            maxRolloverDays: 0,
            quotaSetIn: 'days',
            hideLeaveType: false,
            accrualType: 'NONE',
            isReasonRequired: false,
            cancelUserSettings: false,
            allowLeaveInPast: false,
            broughtForwardExpirationSettings: { enabled: false },
            toil: false,
            version: 3,
          })
        }
      }
      setSubmitProgress(60)

      // Step 5: Rename default dept if changed
      const defaultDept = departments.find((d) => d.isDefault)
      if (defaultDept && defaultDept.name !== defaultDept.initialName) {
        const teamResp = await getTeamByIdQuery({ variables: { id: defaultDept.id } })
        if (!teamResp.data || teamResp.error) throw teamResp.error
        const team = teamResp.data.getTeam
        if (team) {
          const approvers =
            team.approvers?.length > 0
              ? team.approvers.map((a: any) => ({
                  userId: a.id,
                  approverLevels: a.approverLevels as string[],
                }))
              : [{ userId: authUser.id, approverLevels: [ApproversLevelEnum.FIRST] }]

          await postWithRetry({
            eventType: 'TEAM_UPDATED',
            eventGroup: 'TEAM',
            teamId: defaultDept.id,
            name: defaultDept.name,
            isDefault: true,
            users: team.users?.map((u: any) => u.id) ?? [],
            approvers,
            version: 2,
            keepLeaveRequestsOpen: Boolean(team.keepLeaveRequestsOpen),
          })
        }
      }
      setSubmitProgress(70)

      // Step 6: Create new departments
      const newDepts = departments.filter((d) => !d.isDefault)
      for (const dept of newDepts) {
        await postWithRetry({
          eventType: 'TEAM_CREATED',
          eventGroup: 'TEAM',
          name: dept.name,
          approvers: [{ userId: authUser.id, approverLevels: [ApproversLevelEnum.FIRST] }],
          isDefault: false,
          users: [],
          keepLeaveRequestsOpen: false,
          version: 2,
        })
      }
      setSubmitProgress(80)

      // Step 7: Import holidays per location
      const year = new Date().getFullYear()
      for (const loc of locations) {
        const serverLocId = resolveLocId(loc.id)
        try {
          const holidays = await fetchHolidaysForLocation(loc, year)
          if (holidays.length > 0) {
            await postWithRetry(wizardHolidaysImportEvent({ loc, locationId: serverLocId, holidays, year, locale: holidayLocale }))
          }
        } catch {
          // holidays import is non-critical
        }
      }
      setSubmitProgress(100)

      setSavedToDB(true)
      markWizardSavedToDB()
      track('SETUP_WIZARD_CONFIGURATION_SAVED', { locationsCount: locations.length, leaveTypesCount: leaveTypes.length, departmentsCount: departments.length })
      // Advance to import users step - do NOT clear wizard state yet
      setCurrentStep('import')
      saveWizardStep('import')
      setIsSubmitting(false)
    } catch (err: any) {
      notification.error({
        message: formatMessage({ id: 'error.somethingWentWrong' }),
        description: err.message,
        duration: 0,
      })
      clearWizardState(authUser.id, authCompany?.id)
      history.push(FrontendUrls.dashboard)
    }
  }

  const signupLocationId = locations[0]?.id ?? ''

  const renderStep = () => {
    switch (currentStep) {
      case 'locations':
        return (
          <LocationStep
            locations={locations}
            locationLimit={limits.maxLocations}
            onAdd={handleLocationAdd}
            onRemove={handleLocationRemove}
            onConfirm={handleLocationConfirm}
            onSkip={handleSkipForNow}
            onNext={savedToDB ? () => handleStepClick('leaveTypes') : undefined}
            readOnly={savedToDB}
          />
        )
      case 'leaveTypes':
        return (
          <LeaveTypesStep
            leaveTypes={leaveTypes}
            leaveTypesInitialized={leaveTypesInitialized}
            signupLocationId={signupLocationId}
            locations={locations}
            leaveTypeLimit={limits.maxLeaveTypes}
            minLeaveDuration={limits.minLeaveDuration}
            onUpdate={handleLeaveTypesUpdate}
            onInitialized={handleLeaveTypesInitialized}
            onConfirm={handleLeaveTypesConfirm}
            onSkip={handleSkipForNow}
            onNext={savedToDB ? () => handleStepClick('departments') : undefined}
            readOnly={savedToDB}
            planLimitExceeded={overLocationLimit || overLeaveTypeLimit}
          />
        )
      case 'departments':
        return (
          <DepartmentsStep
            departments={departments}
            departmentLimit={limits.maxDepartments}
            loading={needsDepartmentInit && isDepartmentsLoading}
            onUpdate={handleDepartmentsUpdate}
            onConfirm={submitWizard}
            onSkip={handleSkipForNow}
            onNext={savedToDB ? () => handleStepClick('import') : undefined}
            readOnly={savedToDB}
            isSubmitting={isSubmitting}
            submitProgress={submitProgress}
            planLimitExceeded={anyLimitExceeded}
          />
        )
      case 'import':
        return <ImportStep onSkip={handleSkip} onSuccess={handleWizardComplete} />
      default:
        return null
    }
  }

  return (
    <OnboardingLayout currentStep={currentStep} companyName={authCompany?.name ?? ''} firstName={authUser?.name ?? ''} savedToDB={savedToDB} onStepClick={handleStepClick}>
      <SetupSummaryCard
        currentStep={currentStep}
        locations={locations}
        leaveTypes={leaveTypes}
        departments={departments}
        savedToDB={savedToDB}
        onEditLocations={() => handleStepClick('locations')}
        onEditLeaveTypes={() => handleStepClick('leaveTypes')}
        onEditDepartments={() => handleStepClick('departments')}
      />
      {anyLimitExceeded && currentStep !== 'locations' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {overLocationLimit && (
            <Alert type="warning" showIcon message={<IntlMessages id="onboarding.setup.location.overLimit" values={{ limit: limits.maxLocations, count: locations.length }} />} />
          )}
          {overLeaveTypeLimit && (
            <Alert
              type="warning"
              showIcon
              message={<IntlMessages id="onboarding.setup.leaveTypes.overLimit" values={{ limit: limits.maxLeaveTypes, count: leaveTypes.length }} />}
            />
          )}
          {overDepartmentLimit && (
            <Alert
              type="warning"
              showIcon
              message={<IntlMessages id="onboarding.setup.departments.overLimit" values={{ limit: limits.maxDepartments, count: departments.length }} />}
            />
          )}
        </div>
      )}
      {renderStep()}
    </OnboardingLayout>
  )
}

export default OnboardingWizard
